import { useState } from "react";
import { Popover, Tooltip, Upload } from "antd";
import type { UploadChangeParam } from "antd/es/upload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDownUpAcrossLine, faDownload, faUpload, faWarning } from "@fortawesome/free-solid-svg-icons";

//APP
import { motivoSemPermissao, usePermissoesOs } from "../../../../meta/usePermissoesOs";

//COMPONENTS
import ButtonCustom from "../../../../../../../../Components/ButtonCustom";
import { DownloadReprocessarOS } from "../downloadReprocessar";

//PROPS
interface Props {
    cod: number;
    status: number;
    arquivo_processado?: string;
    arquivo_processado_url: string;
    cod_cliente: number;
    propriedade_nome: string;
    talhao_nome: string;
    cod_calcario: number;
    cod_calcario_propriedade: number;
    /** Recarrega a lista após reprocessar a OS. */
    refetch: () => void;
    /** Upload do resultado. O arquivo escolhido abre o preview de CSV, que é
     *  renderizado pelo módulo pai (fora da linha) — daí o callback. */
    onUpload: (info: UploadChangeParam, cod: number) => void;
    /** Motivo para bloquear o envio (ex: sem cliente no filtro global). */
    motivo_bloqueio?: string;
}

/** `customRequest` de fachada: o upload real acontece no envio do preview,
 *  então aqui só resolvemos a requisição para o antd marcar o arquivo como
 *  concluído e disparar o `onChange`. */
const dumpRequest = (options) => {
    const { onSuccess } = options

    setTimeout(() => {
        onSuccess('done')
    }, 500)
}

/**
 * Renderiza a ÚNICA ação de arquivo da linha, espelhando a árvore de decisão
 * da versão em produção. Os quatro ramos são mutuamente exclusivos por
 * construção — não existe estado em que dois botões apareçam juntos.
 *
 * 1. status 9 ............... popover "Baixar análise" + "Reprocessar OS"
 * 2. status 6 ou 7 .......... popover "Baixar resultado" (+ reprocessar
 *                             só quando não há arquivo processado)
 * 3. sem calcário na prop. .. aviso bloqueando o envio
 * 4. demais status .......... upload do resultado, habilitado só em 4 e 8
 */
export const AcaoArquivo: React.FC<Props> = ({
    cod,
    status,
    arquivo_processado,
    arquivo_processado_url,
    cod_cliente,
    propriedade_nome,
    talhao_nome,
    cod_calcario,
    cod_calcario_propriedade,
    refetch,
    onUpload,
    motivo_bloqueio,
}) => {

    //#region STATES
    const [abrir_popover, setAbrir_popover] = useState<boolean>(false);

    //CONST
    const permissions = usePermissoesOs();

    const inconsistente = status === 6 || status === 7;

    if (status === 9 || inconsistente) {
        return (
            <Popover
                trigger='click'
                open={abrir_popover}
                onOpenChange={setAbrir_popover}
                content={() => (
                    <DownloadReprocessarOS
                        arquivo_processado_url={arquivo_processado_url}
                        cod={cod}
                        cod_cliente={cod_cliente}
                        propriedade_nome={propriedade_nome}
                        talhao_nome={talhao_nome}
                        cod_calcario={cod_calcario}
                        cod_calcario_propriedade={cod_calcario_propriedade}
                        refetch={refetch}
                        labelDownload={inconsistente ? 'Baixar resultado' : 'Baixar análise'}
                        // Produção: nos status 6/7 o reprocessar aparece só sem arquivo processado.
                        mostrarReprocessar={inconsistente ? arquivo_processado === '' : true}
                        motivo_bloqueio={motivo_bloqueio}
                    />
                )}
            >
                <Tooltip placement="bottom" title={inconsistente ? 'Baixar resultado' : 'Baixar análise ou Reprocessar OS'}>
                    <span style={{ display: 'inline-flex' }}>
                        <ButtonCustom
                            icon={<FontAwesomeIcon icon={inconsistente ? faArrowDownUpAcrossLine : faDownload} />}
                            type={inconsistente ? 'secondary' : 'primary'}
                            onClick={undefined}
                        />
                    </span>
                </Tooltip>
            </Popover>
        );
    }

    // Sem calcário na propriedade o envio fica bloqueado (substitui o upload).
    if (cod_calcario_propriedade === null) {
        return (
            <Tooltip placement='top' color="red" trigger='hover' title='Informar calcário no cadastro da propriedade'>
                <span style={{ display: 'inline-flex' }}>
                    <ButtonCustom
                        type='secondary'
                        onClick={undefined}
                        disabled
                        icon={<FontAwesomeIcon icon={faWarning} />}
                    />
                </span>
            </Tooltip>
        );
    }

    return (
        <Upload
            accept='.csv,.txt'
            onChange={(file) => onUpload(file, cod)}
            customRequest={dumpRequest}
            showUploadList={false}
        >
            <Tooltip title={motivoSemPermissao(permissions?.alteracao !== 0, 'enviar resultado') ?? motivo_bloqueio ?? 'Enviar resultado da análise'}><samp>
                <ButtonCustom
                    type='secondary'
                    onClick={undefined}
                    icon={<FontAwesomeIcon icon={faUpload} />}
                    // Produção: habilitado somente após o status COLETADA (3).
                    // disabled={permissions?.alteracao === 0 || (status === 1 || status === 2 || status === 3)}
                    disabled={permissions?.alteracao === 0 || !!motivo_bloqueio || (status === 1 || status === 2)}
                />
            </samp></Tooltip>
        </Upload>
    );
}
